import { isIOSDevice } from './micPermission';

function measureRms(analyser, samples) {
  analyser.getByteTimeDomainData(samples);
  let sumSq = 0;
  for (let i = 0; i < samples.length; i += 1) {
    const v = (samples[i] - 128) / 128;
    sumSq += v * v;
  }
  return Math.sqrt(sumSq / samples.length);
}

async function ensureAudioContextRunning(ctx) {
  if (!ctx || ctx.state === 'closed') return false;
  if (ctx.state === 'suspended') {
    try {
      await ctx.resume();
    } catch {
      /* ignore */
    }
  }
  return ctx.state === 'running';
}

/** iOS: reuse one analysis context across re-arms — new contexts stay suspended outside a tap. */
let sharedAnalysisContext = null;

/** Call synchronously inside a user tap/touch before async getUserMedia on iOS. */
export function primeIosAudioAnalysis() {
  if (!isIOSDevice() || typeof window === 'undefined') return;
  const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextCtor) return;
  if (!sharedAnalysisContext || sharedAnalysisContext.state === 'closed') {
    sharedAnalysisContext = new AudioContextCtor();
  }
  try {
    if (sharedAnalysisContext.state === 'suspended') {
      void sharedAnalysisContext.resume();
    }
  } catch {
    /* ignore */
  }
}

function acquireAnalysisContext(AudioContextCtor) {
  if (isIOSDevice()) {
    if (!sharedAnalysisContext || sharedAnalysisContext.state === 'closed') {
      sharedAnalysisContext = new AudioContextCtor();
    }
    return sharedAnalysisContext;
  }
  return new AudioContextCtor();
}

function releaseAnalysisContext(ctx, { close = true } = {}) {
  if (isIOSDevice()) {
    return;
  }
  if (close && ctx && ctx.state !== 'closed') {
    void ctx.close();
  }
}

/**
 * Wait for speech on a live mic stream, then signal when the utterance ends in silence.
 * Does not record — caller starts MediaRecorder on onSpeechStart and stops on onSpeechEnd.
 *
 * @param {MediaStream} stream
 * @param {{ onSpeechStart?: () => void, onSpeechEnd?: () => void, onNoSpeech?: () => void }} callbacks
 * @param {object} [options]
 * @returns {() => void} cleanup
 */
export function watchForSpeechSegment(stream, callbacks, options = {}) {
  const { onSpeechStart, onSpeechEnd, onNoSpeech } = callbacks || {};
  if (!stream) {
    return () => {};
  }

  const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextCtor) {
    return () => {};
  }

  const calibrateMs = options.calibrateMs ?? 320;
  const minSpeechMs = options.minSpeechMs ?? 300;
  const silenceMs = options.silenceMs ?? 1200;
  const maxWaitForSpeechMs = options.maxWaitForSpeechMs ?? 15000;
  const maxUtteranceMs = options.maxUtteranceMs ?? 30000;
  const speechMultiplier = options.speechMultiplier ?? 3.4;
  const silenceMultiplier = options.silenceMultiplier ?? 1.65;
  const detectEndOnly = options.detectEndOnly ?? false;

  const canClone = typeof stream.clone === 'function' && !isIOSDevice();
  const analysisStream = canClone ? stream.clone() : stream;

  const ctx = acquireAnalysisContext(AudioContextCtor);

  const source = ctx.createMediaStreamSource(analysisStream);
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 2048;
  analyser.smoothingTimeConstant = 0.68;
  source.connect(analyser);

  const samples = new Uint8Array(analyser.fftSize);
  let heardSpeech = false;
  let speechStarted = false;
  let speechMs = 0;
  let quietMs = 0;
  let totalMs = 0;
  let utteranceMs = 0;
  let calibratingMs = 0;
  let baselineSum = 0;
  let baselineCount = 0;
  let raf = 0;
  let last = performance.now();
  let settled = false;

  const cleanupAnalysis = () => {
    try {
      source.disconnect();
    } catch {
      /* ignore */
    }
    if (analysisStream !== stream) {
      for (const track of analysisStream.getTracks()) {
        track.stop();
      }
    }
    releaseAnalysisContext(ctx);
  };

  const finish = (reason) => {
    if (settled) return;
    settled = true;
    cancelAnimationFrame(raf);
    cleanupAnalysis();
    if (reason === 'speech-end' && heardSpeech) {
      onSpeechEnd?.();
      return;
    }
    if (!heardSpeech) {
      onNoSpeech?.();
    }
  };

  const tick = () => {
    void ensureAudioContextRunning(ctx);

    const now = performance.now();
    const dt = Math.min(now - last, 100);
    last = now;
    totalMs += dt;
    if (speechStarted) {
      utteranceMs += dt;
    }

    const rms = measureRms(analyser, samples);

    if (calibratingMs < calibrateMs) {
      calibratingMs += dt;
      baselineSum += rms;
      baselineCount += 1;
    }

    const noiseFloor = baselineCount
      ? Math.max(0.004, baselineSum / baselineCount)
      : Math.max(0.004, rms);
    const speechThreshold = Math.max(noiseFloor * speechMultiplier, noiseFloor + 0.011);
    const silenceThreshold = Math.max(noiseFloor * silenceMultiplier, noiseFloor + 0.004);

    if (calibratingMs < calibrateMs) {
      if (!detectEndOnly && totalMs >= maxWaitForSpeechMs) {
        finish('no-speech');
        return;
      }
      raf = requestAnimationFrame(tick);
      return;
    }

    if (rms >= speechThreshold) {
      if (!speechStarted) {
        speechStarted = true;
        if (!detectEndOnly) {
          onSpeechStart?.();
        }
      }
      heardSpeech = true;
      speechMs += dt;
      quietMs = 0;
    } else if (heardSpeech) {
      if (rms <= silenceThreshold) {
        quietMs += dt;
      } else if (rms < speechThreshold) {
        quietMs += dt * 0.4;
      } else {
        quietMs = 0;
      }
    } else if (!detectEndOnly && totalMs >= maxWaitForSpeechMs) {
      finish('no-speech');
      return;
    }

    if (
      heardSpeech &&
      speechMs >= minSpeechMs &&
      quietMs >= silenceMs
    ) {
      finish('speech-end');
      return;
    }
    if (speechStarted && utteranceMs >= maxUtteranceMs) {
      finish('speech-end');
      return;
    }

    raf = requestAnimationFrame(tick);
  };

  const begin = async () => {
    await ensureAudioContextRunning(ctx);
    if (settled) return;
    last = performance.now();
    raf = requestAnimationFrame(tick);
  };

  void begin();

  return () => {
    if (settled) return;
    settled = true;
    cancelAnimationFrame(raf);
    cleanupAnalysis();
  };
}

/** @deprecated Use watchForSpeechSegment */
export function watchForUtteranceEnd(stream, onSilence, options = {}) {
  return watchForSpeechSegment(
    stream,
    {
      onSpeechEnd: () => onSilence?.({ heardSpeech: true }),
      onNoSpeech: () => onSilence?.({ heardSpeech: false }),
    },
    options,
  );
}
